import { useEffect, useState } from "react";
import { router, usePoll } from "@inertiajs/react";
import PageHeader from "@/components/PageHeader";
import TabNavigation from "@/components/tabs/TabNavigation";
import Field from "@/components/fields/Field";
import TagField from "@/components/fields/TagField";
import Button from "@/components/buttons/Button";
import { Artwork } from "@/components/ArtworkSlot";
import buildTrackTabs from "./trackTabs";

interface Artist {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Track {
  id: number;
  title: string;
  lyrics: string | null;
  primary_genre: string | null;
  secondary_genre: string | null;
  primary_genre_label: string | null;
  secondary_genre_label: string | null;
  isrc_code: string | null;
  upc_code: string | null;
  release_date: string | null;
  themes: string | null;
  moods: string | null;
  song_key: string | null;
  tempo_bpm: number | null;
  time_signature: string | null;
  length_seconds: number | null;
  formatted_length: string | null;
  song_key_label: string | null;
  audio_key: string | null;
  description: string | null;
  explicit: boolean;
  artist_id: number;
  created_at: string;
  updated_at: string;
  artist: Artist;
  lyrics_present: boolean;
  artwork_attached: boolean;
}

const ThemesPoller: React.FC = () => {
  usePoll(2000, { only: ["track"] });
  return null;
};

const Show: React.FC<{ track: Track; artwork: Artwork }> = ({ track, artwork }) => {
  const [generatingThemes, setGeneratingThemes] = useState(false);

  useEffect(() => {
    if (generatingThemes && track.themes) setGeneratingThemes(false);
  }, [generatingThemes, track.themes]);

  const handleGenerateThemes = () => {
    setGeneratingThemes(true);
    router.post(
      `/tracks/${track.id}/themes/generate`,
      {},
      { preserveScroll: true },
    );
  };

  return (
    <div className="flex w-full flex-col items-start justify-start gap-5 md:gap-10">
      <PageHeader
        backLink={{ href: "/tracks", text: "Back to all tracks" }}
        title={track.title}
        primaryAction={{ href: `/tracks/${track.id}/edit`, text: "Edit" }}
        artwork={{
          trackId: track.id,
          uploaded: track.artwork_attached,
          thumbnailUrl: artwork.thumbnail_url,
          originalUrl: artwork.original_url,
          alt: `${track.title} artwork`,
        }}
      />
      <TabNavigation
        tabs={buildTrackTabs(track.id, "details")}
      />
      <div className="dark:bg-cream-950 flex w-full flex-col gap-6 rounded-2xl bg-white p-6 shadow md:p-10">
        <Field label="Artist" value={track.artist.name} />
        <Field label="Primary Genre" value={track.primary_genre_label} />
        <Field label="Secondary Genre" value={track.secondary_genre_label} />
        <Field label="ISRC Code" value={track.isrc_code} />
        <Field label="UPC Code" value={track.upc_code} />
        <Field
          label="Release Date"
          value={
            track.release_date
              ? new Date(track.release_date).toLocaleDateString()
              : null
          }
        />
        <div className="flex w-full flex-col gap-3">
          <TagField
            label="Themes"
            value={track.themes}
            loading={generatingThemes}
            hideEmptyState
          >
            <Button
              text="Generate themes"
              type="secondary"
              htmlButtonType="button"
              onClick={handleGenerateThemes}
              disabled={!track.lyrics_present}
            />
          </TagField>
          {generatingThemes && <ThemesPoller />}
          {!track.themes && !generatingThemes && !track.lyrics_present && (
            <span className="text-cream-700 dark:text-cream-300 text-sm">
              Add lyrics to this track before generating themes.
            </span>
          )}
        </div>
        <TagField label="Moods" value={track.moods} />
        <Field label="Song Key" value={track.song_key_label} />
        <Field
          label="Tempo"
          value={track.tempo_bpm ? `${track.tempo_bpm} BPM` : null}
        />
        <Field label="Time Signature" value={track.time_signature} />
        <Field label="Length" value={track.formatted_length} />
        <Field label="Description" value={track.description} />
        <Field label="Content rating" value={track.explicit ? "Explicit" : "Clean"} />
      </div>
    </div>
  );
};

export default Show;
